import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type AggregatedInvestorData = {
  name: string;
  age: number;
  riskScore?: number;
  netWorth?: number;
  managedAssets?: number;
  connectedAssets?: number;
  lastPlanDate?: string;
  numberOfPlanFiles?: number;
};

type CachedInvestorData = {
  data: AggregatedInvestorData;
  timestamp: number; // When the data was cached
};

type AggregatedInvestorDataState = {
  cache: Record<string, CachedInvestorData>; // keyed by cognitoId
};

const initialState: AggregatedInvestorDataState = {
  cache: {},
};

const aggregatedInvestorDataSlice = createSlice({
  name: "aggregatedInvestorData",
  initialState,
  reducers: {
    setCachedInvestorData: (
      state,
      action: PayloadAction<{ cognitoId: string; data: AggregatedInvestorData }>,
    ) => {
      const { cognitoId, data } = action.payload;
      state.cache[cognitoId] = {
        data,
        timestamp: Date.now(),
      };
    },
    clearCachedInvestorData: (state, action: PayloadAction<string>) => {
      const cognitoId = action.payload;
      delete state.cache[cognitoId];
    },
    clearAllCachedInvestorData: (state) => {
      state.cache = {};
    },
  },
});

export const {
  setCachedInvestorData,
  clearCachedInvestorData,
  clearAllCachedInvestorData,
} = aggregatedInvestorDataSlice.actions;

export default aggregatedInvestorDataSlice.reducer;

// Selectors
export const selectCachedInvestorData = (
  state: { aggregatedInvestorData: AggregatedInvestorDataState },
  cognitoId: string,
): CachedInvestorData | undefined => {
  return state.aggregatedInvestorData.cache[cognitoId];
};

export const selectIsCached = (
  state: { aggregatedInvestorData: AggregatedInvestorDataState },
  cognitoId: string,
): boolean => {
  return cognitoId in state.aggregatedInvestorData.cache;
};
